
import React from "react";
import { useQuery } from "@tanstack/react-query";
import { CheckIcon, Filter } from "lucide-react";
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuCheckboxItem,
  DropdownMenuTrigger 
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { DocumentType } from "@/models/DocumentType";
import { documentTypeApi } from "@/services/api";

interface DocumentTypeFilterProps {
  value: number | undefined;
  onChange: (documentTypeId: number | undefined) => void;
}

export function DocumentTypeFilter({ value, onChange }: DocumentTypeFilterProps) {
  const { data: documentTypes = [] } = useQuery({
    queryKey: ["document-types"],
    queryFn: () => documentTypeApi.list(),
    meta: {
      onError: (error: Error) => {
        console.error("Error loading document types:", error);
      }
    }
  });

  const selectedType = value 
    ? documentTypes.find(type => type.id === value) 
    : undefined;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" className="flex items-center gap-2">
          <Filter className="h-4 w-4" />
          <span>Type</span>
          {value && (
            <Badge variant="secondary" className="ml-2">
              {selectedType?.name || "Selected"}
            </Badge>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-56">
        <DropdownMenuLabel>Filter by document type</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuCheckboxItem
          checked={!value}
          onClick={() => onChange(undefined)}
        >
          All Types
        </DropdownMenuCheckboxItem>
        {documentTypes.map((type) => (
          <DropdownMenuCheckboxItem 
            key={type.id}
            checked={value === type.id}
            onClick={() => onChange(type.id)}
          >
            {type.name}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
